import { useMemo } from "react";

import { orderBy } from "lodash-es";

import { useCaseTemplateWithVersions } from "src/cases/queries/templates";
import { DeserializedCaseQueue } from "src/cases/queues/types";
import { JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel } from "src/clients/case-management";

export type ResolvedTemplateVersionStatus =
  | "no_default_version"
  | "no_published_version"
  | "archived"
  | "ok";

export type ResolvedTemplateVersionResult = {
  version:
    | JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel
    | undefined;
  status: ResolvedTemplateVersionStatus;
};

const resolveVersion = (
  defaultTemplateVersion: DeserializedCaseQueue["default_template_version"],
  templateWithVersions: ReturnType<typeof useCaseTemplateWithVersions>["data"],
): ResolvedTemplateVersionResult => {
  if (!defaultTemplateVersion || !templateWithVersions) {
    return { version: undefined, status: "no_default_version" };
  }

  const versions = templateWithVersions.versions ?? [];

  if (defaultTemplateVersion.kind !== "latest") {
    const pinned = versions.find(
      (v) => v.id === defaultTemplateVersion.template_version?.id,
    );
    if (!pinned || pinned.attributes.archived) {
      return { version: undefined, status: "archived" };
    }
    return { version: pinned, status: "ok" };
  }

  const publishedVersions = versions.filter(
    (v) => v.attributes.publish_state === "published" && !v.attributes.archived,
  );
  const latest = orderBy(
    publishedVersions,
    ["attributes.updated_at"],
    ["desc"],
  )[0];

  if (!latest) {
    return { version: undefined, status: "no_published_version" };
  }
  return { version: latest, status: "ok" };
};

export const useResolvedTemplateVersion = (
  queue: DeserializedCaseQueue | undefined,
): ResolvedTemplateVersionResult => {
  const defaultTemplateVersion = queue?.default_template_version;
  const templateId = defaultTemplateVersion?.template?.id;

  const { data: templateWithVersions } =
    useCaseTemplateWithVersions(templateId);

  return useMemo(
    () => resolveVersion(defaultTemplateVersion, templateWithVersions),
    [defaultTemplateVersion, templateWithVersions],
  );
};
